/**
 * copyPieces — la copie d'une activité telle que l'élève la voit : ce que le serveur a déjà (les
 * journaux `step_media_sent`) et ce que la file d'envoi tient encore, en une liste, sans doublon.
 *
 * L'ancien écran tenait une liste locale (`images`) qu'il REMPLAÇAIT par les journaux de la réponse de
 * `/logs` (`ActivityScreen.js:694`) : une photo prise pendant que la précédente partait disparaissait.
 * Ici rien n'est remplacé : une pièce de la file reste tant que le serveur ne l'a pas, et une pièce
 * envoyée se reconnaît à l'id de son journal.
 */
import { tmpKeyOf, type ApiCopyLog, type CopyItem } from "./copyOutbox"

export type CopyPieceStatus = "sent" | "sending" | "failed"

export type CopyPiece = {
    /** `log:<id>` pour une pièce du serveur, `outbox:<id>` pour une pièce de la file. */
    key: string
    uri: string
    status: CopyPieceStatus
    /** Le journal de la pièce : ce que « Supprimer » retire côté serveur. */
    logId: number | null
    /** L'élément de la file : ce que « Réessayer » relance. */
    outboxId: string | null
    /** La durée d'un oral, quand la file la connaît (le serveur ne la sert pas). */
    durationMs: number | null
    /** La clé S3 de la page, quand on la connaît : une pièce de la file et son journal la partagent. */
    tmpKey: string | null
}

/** Une page que le serveur a : son journal, son fichier, la clé S3 dont elle vient s'il la sert. */
export type ServerCopyLog = { id: number; uri: string; tmpKey?: string | null }

/** Les journaux de la copie que le serveur a : `step_media_sent`, dans l'ordre où ils sont arrivés. */
export function serverCopyLogs(logs: ApiCopyLog[] | null | undefined): ServerCopyLog[] {
    return (logs ?? [])
        .filter((log) => log?.action === "step_media_sent" && log.id != null && !!log.work_attachment)
        .map((log) => ({
            id: log.id as number,
            uri: log.work_attachment as string,
            tmpKey: log.work_attachment_tmp_key ?? null,
        }))
        .sort((a, b) => a.id - b.id)
}

/**
 * La copie à l'écran : les pièces du serveur, puis celles de la file pour cette activité (les
 * envoyées que le serveur ne montre pas encore, puis celles qui partent ou ont échoué), moins celles
 * qu'on est en train de supprimer.
 */
export function copyPieces(
    activityId: number | null,
    serverLogs: ServerCopyLog[],
    items: CopyItem[],
    hidden: ReadonlySet<string> = new Set()
): CopyPiece[] {
    if (activityId == null) return []
    const known = new Set(serverLogs.map((log) => log.id))
    // Une page que le serveur a, reconnue à sa clé : la réponse de son journal a pu se perdre, la file
    // la croit en échec. C'est la même page, elle ne se montre pas deux fois et ne repart pas.
    const knownKeys = new Set(serverLogs.map((log) => log.tmpKey).filter((key): key is string => !!key))
    const pieces: CopyPiece[] = serverLogs.map((log) => ({
        key: `log:${log.id}`,
        uri: log.uri,
        status: "sent",
        logId: log.id,
        outboxId: null,
        durationMs: durationOfLog(items, log),
        tmpKey: log.tmpKey ?? null,
    }))
    const mine = items.filter((item) => item.target.activityId === activityId)
    for (const item of mine) {
        if (item.status === "sent" && item.result?.logId != null && known.has(item.result.logId)) continue
        const key = tmpKeyOf(item)
        if (key != null && knownKeys.has(key)) continue
        pieces.push({
            key: `outbox:${item.id}`,
            // Envoyée, la copie locale est effacée : le fichier rangé par le serveur la remplace.
            uri: item.status === "sent" ? (item.result?.uri ?? item.file.uri) : item.file.uri,
            status: item.status === "sent" ? "sent" : item.status === "failed" ? "failed" : "sending",
            logId: item.result?.logId ?? null,
            outboxId: item.id,
            durationMs: item.file.durationMs ?? null,
            tmpKey: key,
        })
    }
    return pieces.filter((piece) => !hidden.has(piece.key))
}

function durationOfLog(items: CopyItem[], log: ServerCopyLog): number | null {
    const own = items.find(
        (item) => item.result?.logId === log.id || (log.tmpKey != null && tmpKeyOf(item) === log.tmpKey)
    )
    return own?.file.durationMs ?? null
}

/**
 * Les éléments de la file qu'une pièce supprimée emporte : le sien, celui de son journal, et celui de
 * sa clé (une pièce que la file croyait en échec alors que le serveur l'avait) — sinon il reviendrait à
 * l'écran en échec, et « Réessayer » recollerait la page que l'élève a retirée.
 */
export function outboxItemsOfPiece(
    piece: Pick<CopyPiece, "outboxId" | "tmpKey">,
    logId: number | null,
    items: CopyItem[]
) {
    return items.filter(
        (item) =>
            item.id === piece.outboxId ||
            (logId != null && item.result?.logId === logId) ||
            (piece.tmpKey != null && tmpKeyOf(item) === piece.tmpKey)
    )
}

/** Ce que la finalisation emporte : les clés S3 de la copie (`pending_attachments`), pour recoller un journal perdu. */
export function pendingAttachments(
    activityId: number | null,
    items: CopyItem[],
    hidden: ReadonlySet<string> = new Set()
): { step: number; tmp_key: string }[] {
    if (activityId == null) return []
    return items
        .filter((item) => item.target.activityId === activityId && !hidden.has(`outbox:${item.id}`))
        .map((item) => ({ step: item.target.mediaStep, tmpKey: tmpKeyOf(item) }))
        .filter((entry): entry is { step: number; tmpKey: string } => entry.tmpKey != null)
        .map((entry) => ({ step: entry.step, tmp_key: entry.tmpKey }))
}

/** Où en est la copie, pour « J'ai fini ». */
export type CopyReadiness = {
    /** Au moins une pièce : « J'ai fini » existe. */
    hasWork: boolean
    /** Une pièce part encore : « J'ai fini » attend qu'elle arrive. */
    inFlight: boolean
    /** Les pièces de la file en échec. */
    failedIds: string[]
}

export function copyReadiness(pieces: CopyPiece[]): CopyReadiness {
    return {
        hasWork: pieces.length > 0,
        inFlight: pieces.some((piece) => piece.status === "sending"),
        failedIds: pieces
            .filter((piece) => piece.status === "failed" && piece.outboxId != null)
            .map((piece) => piece.outboxId as string),
    }
}
